import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useState, useEffect } from 'react';
import { familyInfoUri } from 'Consts/clientUris';
import { householdFamilyInfoUri } from 'Consts/advisorUris';
import AddMoreInfoButton from 'Components/UI/AddMoreInfoButton';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useUserContext from 'Context/UserContext';
import './FamilyInfo.css';
import moment from 'moment';

const FamilyInfo = () => {
  const { getClientInfo: getPrimaryClientInfo, getSpouseInfo, getDependents } = useSwaggerlessContext();
  const [familyData, setFamilyData] = useState([]);
  const { householdId, householdResource } = useSimpleHousehold();
  const primaryClientId = householdResource?.attr('primaryClientId');
  const { user } = useUserContext();

  function getClientInfo(isSpouse) {
    const promise = isSpouse
      ? getSpouseInfo(primaryClientId)
      : getPrimaryClientInfo(primaryClientId);
    return promise
      .then((res) => {
        let members = [];
        if (res?.data.length > 0) {
          members = res.data.map((c) => {
            const clientInfo = c.attributes;
            const nickname = clientInfo.nickname
              ? `"${clientInfo.nickname}"`
              : '';
            const jobTitle = clientInfo.jobTitle
              ? ` - ${clientInfo.jobTitle}`
              : '';
            const yearsOfService = clientInfo.yearsOfService
              ? `(${clientInfo.yearsOfService}yr)`
              : '';
            const jobChangeDescription = clientInfo.jobChangeDescription
              ? ` - ${clientInfo.jobChangeDescription}`
              : '';
            const stateOfResidence = clientInfo.stateOfResidence
              ? ` - ${clientInfo.stateOfResidence} Resident`
              : '';
            return {
              name: `${clientInfo.firstName} ${nickname} ${clientInfo.lastName}`,
              age: `${clientInfo.age}`,
              info: `${clientInfo.employer}${jobTitle}${yearsOfService}${jobChangeDescription}${stateOfResidence}`,
              order: isSpouse ? 1 : 0,
            };
          });
        }
        return members;
      })
      .catch(() => []); // We frequently 404 here, which is okay and we don't want to break the promise chain.  We also don't get a status code here? So no conditional logic possible
  }

  function getChildrenInfo() {
    return getDependents(householdId).then((res) => {
      const data = res && Array.isArray(res.data) ? res.data : [res.data];

      return data
        .filter(c => c.attributes.firstName || c.attributes.lastName)
        .sort((c1, c2) => moment(c1.attributes.birthdate) - moment(c2.attributes.birthdate)) // oldest to youngest
        .map((child) => {
          const grade = child.attributes.grade ? `Grade ${child.attributes.grade}` : '';
          let schoolInfo = '';
          if (child.attributes.schoolType) {
            schoolInfo = child.attributes.schoolType;
            // make school info proper case
            schoolInfo = schoolInfo[0].toUpperCase() + schoolInfo.substring(1);
            schoolInfo = ` - ${schoolInfo}`;
          }
          const educationChanges = child.attributes.educationChanges
            ? ` - ${child.attributes.educationChanges}`
            : '';
          const age = moment().diff(child.attributes.birthdate, 'years');
          return {
            name: `${child.attributes.firstName} ${child.attributes.lastName}`,
            age: `${age}`,
            info: `${grade}${schoolInfo}${educationChanges}`,
            order: 2,
          };
        });
    });
  }

  function renderFamilyMembers() {
    const data = familyData.sort((a, b) => a.order - b.order);
    const formatAge = age => (age === '0' ? 'Infant' : age);

    let addFamilyInfoUri;
    if (user.role === 'Client') {
      addFamilyInfoUri = familyInfoUri;
    } else {
      addFamilyInfoUri = householdFamilyInfoUri(householdId);
    }
    if (data.length > 1) {
      return data.map(member => (
        <div className="family-member" key={`${member.name}-${member.age}`}>
          <div className="member-name">{member.name}</div>
          <div className="member-age">{formatAge(member.age)}</div>
          <div className="member-info">{member.info}</div>
        </div>
      ));
    }
    if (data.length === 1) {
      return (
        <React.Fragment>
          {data.map(member => (
            <div className="family-member" key={`${member.name}-${member.age}`}>
              <div className="member-name">{member.name}</div>
              <div className="member-age">{formatAge(member.age)}</div>
              <div className="member-info">{member.info}</div>
            </div>
          ))}
          <AddMoreInfoButton
            buttonText="Click here to add family information"
            href={addFamilyInfoUri}
          />
        </React.Fragment>
      );
    }
    return (
      <AddMoreInfoButton
        buttonText="Click here to add family information"
        href={addFamilyInfoUri}
      />
    );
  }

  async function getFamilyInfo() {
    const familyInfo = await Promise.all([
      getClientInfo(false),
      getClientInfo(true),
      getChildrenInfo(),
    ]);
    setFamilyData([].concat(...familyInfo));
  }

  useEffect(() => {
    if (primaryClientId > 0) {
      getFamilyInfo();
    }
  }, [primaryClientId]);

  return (
    <div className="family-info">
      <h2>Family Info</h2>
      {renderFamilyMembers()}
    </div>
  );
};

FamilyInfo.propTypes = {
};

const FamilyInfoContainer = FamilyInfo;

export default FamilyInfoContainer;
